/**
 * Phase 2b — DecryptedHeading
 *
 * A drop-in <h1> that renders each character as its own <span> so the
 * decryption-reveal hook can flash a brief "lock-in" pulse on the exact
 * glyph that just resolved. Below the heading, two thin streams of
 * cipher glyphs scroll horizontally, mimicking the parallel decryption
 * rivers from quantumemotion.com — they read as a quantum decapsulation
 * routine resolving the title.
 *
 * Props:
 *   - target          (string)    — the final text to reveal.
 *   - className       (string)    — applied to the <h1>.
 *   - testId          (string)    — data-testid on the <h1>.
 *   - durationMs      (number)    — defaults to 2400ms.
 *   - withStream      (boolean)   — render the decorative cipher river. Default true.
 *   - children        (node)      — extra inline content appended after the text.
 *   - accentColors    (string[])  — (Phase 12.2b) optional palette to cycle each
 *                                   resolved glyph through during the reveal. When
 *                                   provided, each glyph at absolute-index `i`
 *                                   picks `accentColors[i % len]` and that color
 *                                   bleeds through the "is-flash" lock-in pulse +
 *                                   the final "is-resolved" tint. Cycle is
 *                                   stable across renders (deterministic by `i`)
 *                                   so the reveal doesn't flicker on re-render.
 *                                   When omitted, falls back to the default cyan.
 */
import React, { useEffect, useMemo, useState } from "react";
import useDecryptionReveal, { makeCipherStream } from "../../lib/useDecryptionReveal";


export default function DecryptedHeading({
  target,
  className = "qt-docs-h1 qt-docs-decrypt",
  testId,
  durationMs = 2400,
  withStream = true,
  children,
  accentColors,
}) {
  const { text, lockedFlags } = useDecryptionReveal(target, durationMs);
  const display = text || target || "";
  const palette = Array.isArray(accentColors) && accentColors.length > 0
    ? accentColors
    : null;

  return (
    <div className="qt-decrypt-wrap">
      <h1 className={className} data-testid={testId}>
        <span className="qt-decrypt-line" aria-label={target} role="text">
          {splitIntoWords(display, target).map((word, wIdx) => (
            <React.Fragment key={`w-${wIdx}`}>
              <span className="qt-decrypt-word">
                {word.chars.map((entry, i) => {
                  const flagIdx = entry.absIdx;
                  const isResolved =
                    lockedFlags[flagIdx] === false &&
                    flagIdx < (target?.length || 0) &&
                    entry.ch === target?.[flagIdx];
                  const justLocked = lockedFlags[flagIdx] === true;
                  const cls = justLocked
                    ? "qt-decrypt-ch is-flash"
                    : isResolved
                      ? "qt-decrypt-ch is-resolved"
                      : "qt-decrypt-ch is-cipher";
                  // Per-glyph palette tint — only applied to resolved /
                  // just-locked glyphs so cipher noise stays neutral.
                  // We cycle by absolute index (skipping spaces) so
                  // visually adjacent letters get distinct colors.
                  const accent = palette && (isResolved || justLocked)
                    ? palette[flagIdx % palette.length]
                    : null;
                  return (
                    <span
                      key={i}
                      className={cls}
                      aria-hidden="true"
                      style={accent ? { color: accent } : undefined}
                    >
                      {entry.ch}
                    </span>
                  );
                })}
              </span>
              {word.trailingSpace ? " " : null}
            </React.Fragment>
          ))}
        </span>
        {children}
      </h1>
      {withStream && <CipherStream />}
    </div>
  );
}


/**
 * Split the displayed string into words while keeping each character's
 * absolute index, so the per-character `lockedFlags[i]` mapping still
 * lines up after we group them. A "word" here is any run of non-space
 * characters; each run captures whether a trailing space follows so we
 * can render it OUTSIDE the nowrap wrapper (allowing the browser to
 * wrap between words but never inside one).
 */
function splitIntoWords(displayStr, targetStr) {
  const out = [];
  const src = displayStr || "";
  let i = 0;
  while (i < src.length) {
    // Skip leading run of spaces — emit each as its own "empty" word
    // to preserve multi-space gaps (rare in headings, but safe).
    while (i < src.length && src[i] === " ") {
      out.push({ chars: [], trailingSpace: true });
      i += 1;
    }
    if (i >= src.length) break;
    // Collect non-space chars.
    const chars = [];
    while (i < src.length && src[i] !== " ") {
      chars.push({ ch: src[i], absIdx: i });
      i += 1;
    }
    const trailingSpace = i < src.length && src[i] === " ";
    if (trailingSpace) i += 1;
    out.push({ chars, trailingSpace });
  }
  // Reference targetStr to silence unused-arg lint (we may need it for
  // future per-word target alignment if we ever support reflows).
  void targetStr;
  return out;
}


/**
 * A pair of horizontally-scrolling cipher rivers below the H1.
 * They use a long random ciphertext + a CSS marquee so there's zero JS
 * cost after mount.
 */
function CipherStream() {
  // Generated once per mount — the streams are cosmetic, so re-randomising
  // on every keystroke is wasteful.
  const a = useMemo(() => makeCipherStream(320), []);
  const b = useMemo(() => makeCipherStream(320), []);
  // After the first paint, swap the streams to a binary form so the bottom
  // line reads as a "decoded" output. This is a 1-shot effect, not a loop.
  const [decoded, setDecoded] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setDecoded(true), 1800);
    return () => clearTimeout(t);
  }, []);
  const binary = useMemo(() => {
    let s = "";
    for (let i = 0; i < 320; i += 1) s += Math.random() < 0.5 ? "0" : "1";
    return s;
  }, []);

  return (
    <div className="qt-decrypt-streams" aria-hidden="true">
      <div className="qt-decrypt-stream qt-decrypt-stream-cipher">
        <span>{a}</span>
        <span>{a}</span>
      </div>
      <div className={`qt-decrypt-stream qt-decrypt-stream-binary ${decoded ? "is-decoded" : ""}`}>
        <span>{decoded ? binary : b}</span>
        <span>{decoded ? binary : b}</span>
      </div>
    </div>
  );
}
